import { isArray, isString } from 'lodash';
import { CustomComponentTypes } from '../constants/custom-component-types';
import type {
  DocumentSigningValue,
  IEmployeeBenefit,
} from '../types/modular-benefit';

// This helper picks the most relevant entry: the last non-SUPERSEDED request in
// chronological order, falling back to the last entry when all are superseded.
export const getActiveDocumentSigningValue = (
  value: DocumentSigningValue[] | null | undefined
): DocumentSigningValue | null => {
  if (!value || !isArray(value) || value.length === 0) return null;

  const nonSuperseded = value.filter(
    (v) => v.status?.toUpperCase() !== 'SUPERSEDED'
  );

  return nonSuperseded.length > 0
    ? (nonSuperseded.at(-1) ?? null)
    : (value.at(-1) ?? null);
};

export const hasGatingDocumentSigningComponent = (
  benefit?: IEmployeeBenefit
): boolean =>
  benefit?.benefit?.additionalConfig?.steps?.some((step) =>
    step.components?.some(
      (component) =>
        component.type === CustomComponentTypes.DOCUMENT_SIGNING &&
        component.gatesApprovalFlow
    )
  ) ?? false;

const EMPLOYEE_SIGNER_ROLE = 'EMPLOYEE';
const COMPLETED_SIGNER_STATUS = 'COMPLETED';

const normalizeValue = (value: string | undefined): string => {
  if (!value) {
    return '';
  }

  return isString(value) ? value.trim().toUpperCase() : '';
};

export const isEmployeeDocumentSigningCompleted = (
  value: DocumentSigningValue | null | undefined
): boolean => {
  const signers = value?.signers ?? [];
  if (!isArray(signers)) {
    return false;
  }

  return signers.some((signer) => {
    const signerRole = signer.role;
    const signerStatus = signer.status;

    return (
      normalizeValue(signerRole) === EMPLOYEE_SIGNER_ROLE &&
      normalizeValue(signerStatus) === COMPLETED_SIGNER_STATUS
    );
  });
};

export const getDocumentSigningViewUrl = (
  value: DocumentSigningValue | null | undefined
): string => {
  const url = value?.signedDocumentUrl;

  return url ?? '';
};

export const buildCompletedDocumentSigningValue = (
  currentValue: DocumentSigningValue | null | undefined,
  signingRequestId: number | null
): DocumentSigningValue => {
  const existingSigners = valueOrEmptyArray(currentValue?.signers);
  const updatedSigners = getUpdatedEmployeeSigners(existingSigners);

  return {
    ...currentValue,
    ...(signingRequestId != null && { signingRequestId }),
    signers: updatedSigners,
  };
};

const valueOrEmptyArray = <T>(value: T[] | undefined): T[] => {
  if (!isArray(value)) {
    return [];
  }

  return value;
};

const isEmployeeSigner = (role: string | undefined): boolean => {
  return normalizeValue(role) === EMPLOYEE_SIGNER_ROLE;
};

const getUpdatedEmployeeSigners = (
  signers: NonNullable<DocumentSigningValue['signers']>
): NonNullable<DocumentSigningValue['signers']> => {
  const hasEmployeeSigner = signers.some((signer) =>
    isEmployeeSigner(signer.role)
  );
  if (hasEmployeeSigner) {
    return signers.map((signer) =>
      isEmployeeSigner(signer.role)
        ? { ...signer, status: COMPLETED_SIGNER_STATUS }
        : signer
    );
  }

  return [
    ...signers,
    {
      role: EMPLOYEE_SIGNER_ROLE,
      order: 0,
      status: COMPLETED_SIGNER_STATUS,
    },
  ];
};
